// The fields of each drop zone, shown as chips ON the chart — under the
// category axis, at the value axis, at the legend — so the author can read
// which column drives which axis and drag one across axes (or drop a new field
// onto an axis) without opening the drop zones. Zones with no natural place on
// a chart (card positions, feed rows, hierarchy levels…) sit in a strip above.

import { useState } from 'react'
import type { ColumnGroup, VisualColumn } from './types'
import { SlotIcon } from './slotIcons'
import { groupHint, type ZoneDrop } from './DropZones'

type Region = 'x' | 'y' | 'legend' | 'top'
const RCV: Record<string, Region> = { rows: 'x', columns: 'legend', values: 'y' }
const BY_CLASS: Record<string, Record<string, Region>> = {
  insightcolumnchart: RCV, insightlinechart: RCV, insightstackedchart: RCV, insightcolumnlinechart: RCV,
  insightscatterchart: RCV, insightbubblechart: RCV,
  insightwaterfall: { category: 'x', value: 'y' },
  insightstackbarlinechart: { rows: 'x', stack: 'y', lines: 'legend' },
  insightbarchart: { rows: 'y', columns: 'legend', values: 'x' },
  insightpiechart: { rows: 'legend', columns: 'legend', values: 'x' },
  insightheatmap: { rows: 'y', columns: 'legend', values: 'x' },
  insightpivottable: { rows: 'y', columns: 'legend', values: 'x' },
  insighttaskscheduler: { rows: 'y', columns: 'legend', values: 'x' },
  insighthtml: { groups: 'y', rows: 'y', columns: 'legend', values: 'x' },
  insightfunnel: { stage: 'y', value: 'x' },
  insightradar: { axis: 'x', value: 'y', series: 'legend' },
  insighttreemap: { label: 'x', parent: 'legend', value: 'y' },
  insightsankey: { source: 'y', target: 'legend', value: 'x' },
}

export interface AxisShelvesProps {
  part: 'top' | 'bottom'
  className: string
  groups: ColumnGroup[]
  iconOf: (g: ColumnGroup) => string
  readOnly: boolean
  dragging: boolean
  canDrop: (std: string) => ZoneDrop
  colLabel: (c: VisualColumn) => string
  colAgg: (c: VisualColumn) => string
  colDot: (c: VisualColumn) => string
  onDrop: (std: string, beforeColId?: number) => void
  onDragStartCol: (colId: number) => void
  onDragEnd: () => void
  onEdit: (colId: number) => void
}

export default function AxisShelves(p: AxisShelvesProps) {
  const [hot, setHot] = useState<string | null>(null)
  const map = BY_CLASS[(p.className || '').toLowerCase()] || {}
  const regionOf = (g: ColumnGroup): Region => map[(g.StandardName || '').toLowerCase()] || 'top'
  const shown = (g: ColumnGroup) => {
    const n = (g.COLUMNS || []).length
    if (n) return true
    if (p.dragging) return p.canDrop(g.StandardName).ok
    if (Number(g.MinColumns || 0) >= 1) return true
    // The split/legend slot is offered as a hint even when empty.
    return !p.readOnly && regionOf(g) === 'legend'
  }
  const byRegion = (r: Region) => p.groups.filter((g) => regionOf(g) === r && shown(g))

  const slot = (g: ColumnGroup) => {
    const cols = g.COLUMNS || []
    const d = p.dragging ? p.canDrop(g.StandardName) : null
    const cls = ['vdo-slotc', d ? (d.ok ? 'ok' : 'no') : '', hot === g.StandardName ? 'hot' : '',
      Number(g.MinColumns || 0) >= 1 && !cols.length ? 'req' : ''].filter(Boolean).join(' ')
    const drop = (beforeColId?: number) => (e: React.DragEvent) => {
      e.preventDefault(); e.stopPropagation(); setHot(null)
      if (!p.readOnly && p.canDrop(g.StandardName).ok) p.onDrop(g.StandardName, beforeColId)
    }
    const ghost = !cols.length && !p.dragging && regionOf(g) === 'legend'
    return (
      <div
        key={g.StandardName}
        className={cls}
        onDragOver={(e) => {
          if (!p.dragging || p.readOnly || !p.canDrop(g.StandardName).ok) return
          e.preventDefault(); e.stopPropagation()
          if (hot !== g.StandardName) setHot(g.StandardName)
        }}
        onDragLeave={() => setHot((h) => (h === g.StandardName ? null : h))}
        onDrop={drop()}
      >
        {!ghost && (
          <span className="vdo-slotc-n" title={groupHint(g) || g.GroupName}>
            <SlotIcon token={p.iconOf(g)} size={13} />{g.GroupName}
          </span>
        )}
        {cols.map((c) => (
          <button
            key={c.COLID}
            type="button"
            className="vdo-achip"
            draggable={!p.readOnly}
            onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(c.COLID)); p.onDragStartCol(c.COLID) }}
            onDragEnd={p.onDragEnd}
            onDragOver={(e) => {
              if (!p.dragging || p.readOnly || !p.canDrop(g.StandardName).ok) return
              e.preventDefault(); e.stopPropagation()
            }}
            onDrop={drop(c.COLID)}
            onClick={() => p.onEdit(c.COLID)}
            title="Drag to another axis or reorder · click to edit"
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.colDot(c), flexShrink: 0 }} />
            {p.colLabel(c)}
            {p.colAgg(c) && <span className="vdo-agg">{p.colAgg(c)}</span>}
          </button>
        ))}
        {!cols.length && (
          <span className="vdo-slotc-e">
            {p.dragging ? 'drop here' : ghost ? `+ ${g.StandardName.toLowerCase() === 'columns' ? 'Split by' : g.GroupName}` : 'needed'}
          </span>
        )}
      </div>
    )
  }

  if (p.part === 'bottom') {
    const x = byRegion('x')
    return x.length ? <div className="vdo-shelf center">{x.map(slot)}</div> : null
  }
  const top = byRegion('top'), y = byRegion('y'), legend = byRegion('legend')
  return (
    <>
      {top.length > 0 && <div className="vdo-shelf" style={{ marginBottom: 8 }}>{top.map(slot)}</div>}
      {(y.length > 0 || legend.length > 0) && (
        <div className="vdo-shelf split">
          <div className="vdo-shelf">{y.map(slot)}</div>
          <div className="vdo-shelf" style={{ justifyContent: 'flex-end' }}>{legend.map(slot)}</div>
        </div>
      )}
    </>
  )
}
